import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Router, Switch, Route, useLocation } from 'react-router-dom';
import Footer from '../Footer';
import Pricing from '../../pages/Pricing';
import Login from '../../pages/Login';
import ForgotPassword from '../../pages/ForgotPassword';
import PrivateRoute from '../../PrivateRoute';
import NavbarKON from '../NavbarKON';
import NotFound from '../../pages/NotFound';
import history from '../../services/history';
import Killshot from '../../pages/Killshot';
import Settings from '../../pages/Settings';
import SalesStream from '../../pages/SalesStream';

const { KILLSHOT_V2_URL, KILLSHOT_V2_ENABLED } = process.env;
const IS_V2_ENABLED = KILLSHOT_V2_ENABLED;

const SettingsRedirect = () => {
  const redirectUrl = `${KILLSHOT_V2_URL}/settings`;
  window.location.replace(redirectUrl);
  return null;
};

const SalesStreamRedirect = () => {
  const redirectUrl = `${KILLSHOT_V2_URL}/sales-stream`;
  window.location.replace(redirectUrl);
  return null;
};

const NavigationContent = ({ userEmail, switchThemeMode }) => {
  const location = useLocation();

  const favicon = {
    nyan:
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAMAAAAoLQ9TAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAMAUExURQ8YIQwbLgQeNxMXMBMeMh0XOR4fMQ0gPhUiNRUnOyMcOyEpLCEvMCQ+PTgzMAIpSgYqTgcnUAAuWgUuXAorVAsuVAgvVg4rUw0qVwwtUAwvWQ4yTAYzVAEwXgAwXwEyXQAyXgA1WwM0XAE0XwE3XQI3XwcwXgY1XwA4XgoxWgswXAk2Xw4xWQ04WxMpQRUpRBMoVxEtVRUrVBUtWREyXRc5VQAvYAAvYQAsZwAuZwAxYAAwYQExYgAwYwIyYAAyYQAyYwAwZQAxZgEzZAAyZQAyZwE0YQE0YgA0YwE2YwA1ZAI0ZQA0ZgE0ZwA3ZAA3ZQA2ZwQwYQYyYwQyZAUzZQY1YQQ0YgY0Zgc2ZAQ3ZgIxaQAxagEzaAAyaQAyawAzbAAybgA0aAA0aQA1awE2aAI3aQA3agM0bgE2bAE3bQA2bwQwbwc3aAQ3bAU2bgA5YwQ4YAU4ZwU6ZgA4aAM4bAQ4aAQ5awgyZgwyYQ40ZQg4aAA1cAA1cRMpYhQrYRUrZRcsYyMuTi84SSg/XwpWeBlXcCpDWTFDUShTZDVAYFNIG2pOAGFdE05RWE5VXVRKbFRdZExnVltoeXxLT2xDZWNHcHpYc25haG5gbXJveHpueHV2eAtehnFWg3lUh3tWinxXjXpag25jg2Z1iHJihmWXlGmbkn+OpYJZAIRfV4NGf4tOd4FTb5BbiZh9jqZ2nqxxoauocd/OduXKb4aLkYiDmpOEl5KQk5iTl5GVoIi1oa6HnKOTnrWbpMCovtaQzt+W29+f09Soy9qt1N+41+WYxuKd1uWZ1+yc2+yV4/GR6PqY7fmd6v6b6+mszeal2+Wr1OC23uO53euq4PCm4/Sm4Pqh8dLVhODE0wAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAJNhIKMAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAZdEVYdFNvZnR3YXJlAFBhaW50Lk5FVCB2My41LjVJivzgAAABG0lEQVQoUwEQAe/+AHxhJSFMaUVIR05zTVNURGEAe2MjcDtBYm1KSkBNNmw9UABdXDxTUlduOVt0TUtDQEpIAE09XHcYM35/fYAwGCpVSkoAZGJoE4GinZ+gnqGkiRkdZQA7QEQ0j9LP1MbB0MS1LylGAHkmKzKV08nWwKqwwraCNRwACwwNAJTVy8q/ra+WmLmGGwCoi4yKk8XNzKu7rry4tKMuALOy17GpzsjHrL2Xt5uQpwcApaa6kQ7Yw9G+mpmNjpKDMQCchIWIhwYKBQMECAEJAhR4AChvP1EaFxEsKRYVEA8tJz4ASU9eOHc8WTphYTZ6HxJyPwBnQnVJElg8aV5qYzd2O3FHAGtaICQiI0RfYHtfXWZEHlYX2mbiHrA6rAAAAABJRU5ErkJggg==',
  };

  const usePageMeta = (title, faviconUrl) => {
    useEffect(() => {
      // Update document title
      document.title = title;

      // Update favicon
      const link = document.querySelector("link[rel='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = faviconUrl;
      document.head.appendChild(link);

      return () => {
        document.title = 'Killshot';
        // By default set the icon as the base 64 that we had for Killshot
        link.href = favicon.nyan;
      };
    }, [title, faviconUrl]);
  };

  // Check if the current path is "/sales-stream"
  const isSalesStream = location.pathname === '/sales-stream';

  // Determine the title and favicon URL based on the current route
  const getPageMeta = path => {
    switch (path) {
      case '/sales-stream':
        return {
          title: 'Sales Stream',
          faviconUrl: '/sales-stream.ico',
        };
      default:
        return {
          title: 'Killshot',
          faviconUrl: favicon.nyan,
        };
    }
  };

  const { title, faviconUrl } = getPageMeta(location.pathname);

  // Update the tab title and favicon based on the current route
  usePageMeta(title, faviconUrl);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <div style={{ flex: 1 }}>
        {/* Conditionally render NavbarKON based on the current path */}
        {!isSalesStream && <NavbarKON userEmail={userEmail} />}
        <Switch>
          <PrivateRoute exact path="/" component={Killshot} />
          <PrivateRoute path="/event/pricing/:event_id" component={Pricing} />
          <PrivateRoute path="/settings" component={IS_V2_ENABLED ? SettingsRedirect : Settings} />
          <PrivateRoute path="/sales-stream" component={IS_V2_ENABLED ? SalesStreamRedirect : SalesStream} />
          <Route path="/login" component={Login} />
          <Route path="/login/:redirect_url" component={Login} />
          <Route path="/forgot-password" component={ForgotPassword} />
          <Route path="*" component={NotFound} />
        </Switch>
      </div>
      {/* Conditionally render Footer based on the current path */}
      {!isSalesStream && <Footer switchThemeMode={switchThemeMode} />}
    </div>
  );
};

const Navigation = ({ userEmail, switchThemeMode }) => (
  <Router history={history}>
    <NavigationContent userEmail={userEmail} switchThemeMode={switchThemeMode} />
  </Router>
);

Navigation.propTypes = {
  userEmail: PropTypes.string.isRequired,
  switchThemeMode: PropTypes.func.isRequired,
};

NavigationContent.propTypes = {
  userEmail: PropTypes.string.isRequired,
  switchThemeMode: PropTypes.func.isRequired,
};

export default Navigation;
